import React, { useEffect, useMemo, useState } from "react";
import classes from './BalanceSheetReport.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import { useLocation } from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import api from "../../Components/API/Api";

import {
    BarChart2,
    Filter,
    CalendarClock,
    Mail,
    ArrowLeft,
    Printer,
    Download,
    Settings
} from 'lucide-react';
import Footer from "../../Components/Footer/Footer";

const BalanceSheetReport = () => {

    const location = useLocation();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [records, setRecords] = useState([]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
    };

    const fetchFinancialPosition = async (params = {}) => {
        setIsLoading(true);
        setError("");
        try {
            const res = await api.get('/v1/reports/financial-position', {
                params,
            });
            const payload = res?.data?.data;
            const recs = Array.isArray(payload?.records) ? payload.records : [];
            setRecords(recs);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to fetch balance sheet';
            setError(msg);
            setRecords([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchFinancialPosition();
    }, []);

    const handleRunReport = (e) => {
        e.preventDefault();
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        fetchFinancialPosition(params);
    };

    const totals = useMemo(() => {
        const bySection = {};
        for (const rec of records) {
            const section = rec?.description || '';
            const sum = (rec?.categories || []).reduce((acc, c) => acc + (Number(c?.total) || 0), 0);
            bySection[section] = (bySection[section] || 0) + sum;
        }
        return {
            assets: bySection['Assets'] || 0,
            liabilities: bySection['Liabilities'] || 0,
            equity: bySection['Equity'] || 0,
        };
    }, [records]);

    const handlePrint = () => {
        window.print();
    };

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname]} />

            <div className={classes.Container}>
                {/* Action Buttons */}
                <div className={classes.actions}>
                    <button className={classes.btns}>New Sales Invoice</button>
                    <button className={classes.btns}>New Purchase Invoice</button>
                    <button className={classes.btns}>All Invoices</button>
                    <button className={`${classes.btns} ${classes.greens}`}>Product & Services</button>
                </div>

                {/* Back Button */}
                <div className={classes.matron}>
                    <div>
                        <button
                            className={classes.backButton}
                            onClick={() => navigate(-1)}
                        >
                            <ArrowLeft size={16} style={{ marginRight: '6px' }} />
                        </button>
                    </div>

                    <div>
                        <p className={classes.toplea}>Balance Sheet</p>
                        <p className={classes.topl}>Financial position as of 7/29/2025</p>
                    </div>
                </div>

                <div className={classes.act}>
                    <form className={classes.form} onSubmit={handleRunReport}>
                        <div className={classes.group}></div>

                        <div className={classes.group1}>
                            <label>Start Date:</label>
                            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        </div>
                        <div className={classes.group1}>
                            <label>End Date:</label>
                            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        </div>

                        <div className={classes.group1}>
                            <button className={classes.greenBtn} type="submit" disabled={isLoading}>
                                <BarChart2 size={16} style={{ marginRight: "6px" }} />
                                {isLoading ? 'Running...' : 'Run Report'}
                            </button>
                        </div>
                    </form>

                    <div className={classes.iconbars}>
                        <div className={classes.iconbarr}>
                            <div className={classes.iconbar}>
                                <Filter size={16} style={{ marginRight: "6px" }} />
                                Filter
                            </div>
                            <div className={classes.iconbar}>
                                <CalendarClock size={16} style={{ marginRight: "6px" }} />
                                Schedule
                            </div>
                        </div>
                    </div>
                </div>

                {/* Report Content (this is what will be printed) */}
                <div className={classes.containere}>
                    <div className={classes.iconbarss}>
                        <div className={classes.iconbar}>
                            <Mail size={16} />
                        </div>
                        <div className={classes.iconbar}>
                            <Printer size={16} style={{ cursor: 'pointer' }} onClick={handlePrint} />
                        </div>
                        <div className={classes.iconbar}>
                            <Download size={16} />
                        </div>
                        <div className={classes.iconbar}>
                            <Settings size={16} />
                        </div>
                    </div>
                    <header className={classes.headers}>
                        <h1 className={classes.companyName}>Promix Accounting Ltd</h1>
                        <h2 className={classes.title}>Balance Sheet</h2>
                        <p className={classes.period}>For the period ended January 31, 2025</p>
                        <button className={classes.unaudited}>unaudited</button>
                    </header>

                    <div className={classes.divider}></div>

                    {error && (
                        <div className={classes.row}>
                            <p className={classes.label} style={{ color: 'red' }}>{error}</p>
                        </div>
                    )}

                    {/* Assets */}
                    {records.filter(r => r?.description === 'Assets').map((rec, idx) => (
                        <section className={classes.section} key={`assets-${idx}`}>
                            <h3 className={classes.sectionTitle}>Assets</h3>
                            {(rec?.categories || []).map((cat, ci) => (
                                <React.Fragment key={`a-cat-${ci}`}>
                                    <div className={classes.rowcor}>
                                        <p className={classes.labelcor}>{cat?.description}</p>
                                    </div>
                                    {(cat?.postings || []).map((p, pi) => (
                                        <div className={classes.row} key={`a-post-${ci}-${pi}`}>
                                            <p className={classes.label}>{p?.name}</p>
                                            <p className={classes.amount}>{Number(p?.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                        </div>
                                    ))}
                                    <div className={classes.totalRow}>
                                        <p className={classes.labelcor}>Total {cat?.description}</p>
                                        <p className={classes.labelcor}>{Number(cat?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                    </div>
                                </React.Fragment>
                            ))}
                            <div className={classes.row3}>
                                <p className={classes.label}>TOTAL ASSETS</p>
                                <p className={classes.label}>{Number(totals.assets).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                            </div>
                        </section>
                    ))}

                    {/* Liabilities & Equity */}
                    <section className={classes.section}>
                        <h3 className={classes.sectionTitle}>LIABILITIES & EQUITY</h3>

                        {/* Liabilities */}
                        {records.filter(r => r?.description === 'Liabilities').map((rec, idx) => (
                            <React.Fragment key={`liab-${idx}`}>
                                {(rec?.categories || []).map((cat, ci) => (
                                    <React.Fragment key={`l-cat-${ci}`}>
                                        <div className={classes.rowcor2}>
                                            <p className={classes.labelcor4}>{cat?.description}</p>
                                        </div>
                                        {(cat?.postings || []).map((p, pi) => (
                                            <div className={classes.row} key={`l-post-${ci}-${pi}`}>
                                                <p className={classes.label}>{p?.name}</p>
                                                <p className={classes.amount}>{Number(p?.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                            </div>
                                        ))}
                                        <div className={classes.rowcor3}>
                                            <p className={classes.labelcor5}>Total {cat?.description}</p>
                                            <p className={classes.labelcor5}>{Number(cat?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                        </div>
                                    </React.Fragment>
                                ))}
                                <div className={classes.rowcor2}>
                                    <p className={classes.labelcor4}>TOTAL LIABILITIES</p>
                                    <p className={classes.labelcor4}>{Number(totals.liabilities).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                </div>
                            </React.Fragment>
                        ))}

                        {/* Equity */}
                        {records.filter(r => r?.description === 'Equity').map((rec, idx) => (
                            <React.Fragment key={`eq-${idx}`}>
                                <div className={classes.rowcor4}>
                                    <p className={classes.labelcor6}>Equity</p>
                                </div>
                                {(rec?.categories || []).map((cat, ci) => (
                                    <React.Fragment key={`e-cat-${ci}`}>
                                        {(cat?.postings || []).map((p, pi) => (
                                            <div className={classes.row} key={`e-post-${ci}-${pi}`}>
                                                <p className={classes.label}>{p?.name}</p>
                                                <p className={classes.amount}>{Number(p?.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                            </div>
                                        ))}
                                        <div className={classes.rowcor4}>
                                            <p className={classes.labelcor6}>Total {cat?.description}</p>
                                            <p className={classes.labelcor6}>{Number(cat?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                        </div>
                                    </React.Fragment>
                                ))}
                                <div className={classes.rowcor4}>
                                    <p className={classes.labelcor6}>TOTAL EQUITY</p>
                                    <p className={classes.labelcor6}>{Number(totals.equity).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                </div>
                            </React.Fragment>
                        ))}
                    </section>

                    <div className={classes.netProfitRow}>
                        <p className={classes.netProfitLabel}>TOTAL LIABILITIES & EQUITY</p>
                        <p className={classes.netProfitAmount}>{Number(totals.liabilities + totals.equity).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                    </div>

                </div>
            </div>

            <Footer />
        </div>
    );
};

export default BalanceSheetReport;
